import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { HiChatAlt2, HiExternalLink, HiX } from "react-icons/hi";
import ConversationBrowser from "./ConversationBrowser";
import { useChat } from "./ChatProvider";
import { useIsMobile } from "./useMedia";
import { useFullScreenView } from "../../utils/viewport";
import { useBackToClose } from "../../utils/backToClose";

// Header chat icon with the unread badge; opens the conversation panel (full screen on phones).
// variant "bar": a plain round icon button for the phone app bar.
function ChatHeaderButton({ variant = "tile" }) {
  const { totalUnread, openConversation, status } = useChat();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const mobile = useIsMobile();
  useFullScreenView(mobile && open);
  useBackToClose(mobile && open, () => setOpen(false));

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      // Ignore clicks inside the panel, on the toggle, or in chat modals/popovers portalled to <body>.
      if (panelRef.current?.contains(e.target) || buttonRef.current?.contains(e.target)) return;
      if (e.target.closest?.('[role="dialog"], [data-tippy-root]')) return;
      setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && !document.querySelector('[role="dialog"]') && setOpen(false);
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const select = (conversationId) => {
    openConversation(conversationId);
    setOpen(false);
  };

  return (
    <>
      <motion.button
        ref={buttonRef}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setOpen((v) => !v)}
        className={
          variant === "bar"
            ? "relative w-10 h-10 flex items-center justify-center rounded-full text-app-secondary active:bg-app-hover transition-colors"
            : "relative w-10 h-10 flex items-center justify-center rounded-xl bg-blue-50 dark:bg-gray-800 border border-blue-100 dark:border-gray-700 shadow-sm hover:bg-blue-100 dark:hover:bg-gray-700 transition-colors"
        }
        aria-label={totalUnread ? `Chat, ${totalUnread} unread` : "Chat"}
        data-tour="header-chat"
        aria-expanded={open}
      >
        <HiChatAlt2 className={`${variant === "bar" ? "w-6 h-6" : "w-5 h-5"} text-[#2470B7] dark:text-blue-300`} />
        {totalUnread > 0 && (
          <span className={`absolute ${variant === "bar" ? "top-0 right-0" : "-top-1.5 -right-1.5"} min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white dark:ring-gray-900`}>
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
        {status === "connected" && !totalUnread && (
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-green-500" aria-hidden="true" />
        )}
      </motion.button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            className={`z-[70] bg-white dark:bg-gray-900 overflow-hidden ${
              mobile
                ? "mobile-fullscreen"
                : "fixed top-[76px] right-4 w-[380px] max-w-[calc(100vw-2rem)] h-[min(600px,calc(100vh-96px))] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700"
            }`}
            data-chat-panel
          >
            <ConversationBrowser
              onSelect={select}
              headerExtra={
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      navigate("/admin/chat");
                    }}
                    className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                    title="Open full chat"
                    aria-label="Open full chat page"
                  >
                    <HiExternalLink className="w-5 h-5" />
                  </button>
                  {mobile && (
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                      aria-label="Close chat panel"
                    >
                      <HiX className="w-5 h-5" />
                    </button>
                  )}
                </>
              }
            />
          </div>,
          document.body
        )}
    </>
  );
}

export default ChatHeaderButton;
